import React, { useEffect, useState } from "react";

export interface DownloadNotification {
  id: string;
  type: 'downloading' | 'success' | 'error' | 'retry';
  message: string;
  filename?: string;
  progress?: number;
  total?: number;
  autoHide?: boolean;
}

interface DownloadToastProps {
  notification: DownloadNotification;
  onDismiss: (id: string) => void;
}

export const DownloadToast: React.FC<DownloadToastProps> = ({
  notification,
  onDismiss,
}) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (notification.autoHide !== false && notification.type === 'success') {
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(() => onDismiss(notification.id), 300);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification, onDismiss]);

  const getToastStyles = () => {
    const baseStyles = "flex items-center space-x-3 p-4 rounded-lg shadow-lg border transition-all duration-300";
    
    if (!isVisible) {
      return `${baseStyles} opacity-0 transform translate-x-full`;
    }

    switch (notification.type) {
      case 'downloading':
        return `${baseStyles} bg-blue-50 border-blue-200 text-blue-800`;
      case 'success':
        return `${baseStyles} bg-green-50 border-green-200 text-green-800`;
      case 'error':
        return `${baseStyles} bg-red-50 border-red-200 text-red-800`;
      case 'retry':
        return `${baseStyles} bg-orange-50 border-orange-200 text-orange-800`;
      default:
        return `${baseStyles} bg-gray-50 border-gray-200 text-gray-800`;
    }
  };

  const getIcon = () => {
    switch (notification.type) {
      case 'downloading':
        return (
          <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin shrink-0" />
        );
      case 'success':
        return (
          <div className="w-5 h-5 bg-green-600 rounded-full flex items-center justify-center shrink-0">
            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        );
      case 'error':
        return (
          <div className="w-5 h-5 bg-red-600 rounded-full flex items-center justify-center shrink-0">
            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        );
      case 'retry':
        return (
          <div className="w-5 h-5 border-2 border-orange-600 border-t-transparent rounded-full animate-spin shrink-0" />
        );
      default:
        return null;
    }
  };

  return (
    <div className={getToastStyles()}>
      {getIcon()}
      
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{notification.message}</div>
        {notification.filename && (
          <div className="text-xs opacity-75 truncate">
            {notification.filename}
          </div>
        )}
        {notification.progress !== undefined && notification.total !== undefined && (
          <div className="mt-1">
            <div className="text-xs opacity-75">
              {notification.progress} of {notification.total} files
            </div>
            <div className="w-full h-1 bg-black bg-opacity-20 rounded-full mt-1">
              <div 
                className="h-1 bg-current rounded-full transition-all duration-300"
                style={{ width: `${(notification.progress / notification.total) * 100}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Dismiss button */}
      <button
        onClick={() => {
          setIsVisible(false);
          setTimeout(() => onDismiss(notification.id), 300);
        }}
        className="shrink-0 p-1 rounded-full hover:bg-black hover:bg-opacity-10 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};